import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import http from '@lib/http-client'
import {
  IMemberSaving,
  IMemberSavingsDashboardItem,
  MemberSavingsCategory,
  MemberSavingsDashboardPayload,
  MemberSavingsStatus,
  MemberSavingsStore
} from './types'

type MemberSavingPostBody = {
  client_cost_savings: number
  member_cost_savings: number
  status: MemberSavingsStatus
}

const normalizeSavingsAmount = (value?: number | null): number => value ?? 0

const postMemberSaving = async (
  userId: number,
  savingId: number,
  body: MemberSavingPostBody
): Promise<{ success: boolean }> => {
  try {
    const response = await http.post(
      // the trailing slash is canonical: without it the server answers 307
      // and the browser drops the request, since a preflight cannot redirect
      `/user/${userId}/member_savings/${savingId}/`,
      body
    )

    return { success: Boolean(response?.success) }
  } catch (error) {
    console.error('Error updating member saving:', error)
    return { success: false }
  }
}

export const useMemberSavingsStore = create<MemberSavingsStore>()(
  devtools(() => ({
    getMemberSavings: async (
      userId: number,
      category: MemberSavingsCategory,
      status: MemberSavingsStatus = 'PENDING'
    ): Promise<IMemberSaving[]> => {
      try {
        const params = new URLSearchParams()
        params.append('status', status)
        params.append('category', category)

        const response = await http.get(
          `/user/${userId}/member_savings?${params.toString()}`
        )

        if (!response || !Array.isArray(response)) {
          return []
        }

        return response as IMemberSaving[]
      } catch (error) {
        console.error('Error fetching member savings:', error)
        return []
      }
    },

    getMemberSavingsDashboard: async ({
      professional_ids = [],
      company_ids = [],
      status = 'PENDING'
    }: MemberSavingsDashboardPayload = {}): Promise<
      IMemberSavingsDashboardItem[]
    > => {
      try {
        const params = new URLSearchParams()
        params.append('status', status)

        if (professional_ids.length > 0) {
          params.append('professional_id', professional_ids.join(','))
        }
        if (company_ids.length > 0) {
          params.append('company_id', company_ids.join(','))
        }

        const response = await http.get(
          `/dashboard/member_savings?${params.toString()}`
        )

        if (!response || !Array.isArray(response)) {
          return []
        }

        return response as IMemberSavingsDashboardItem[]
      } catch (error) {
        console.error('Error fetching member savings dashboard:', error)
        return []
      }
    },

    updateMemberSavingCosts: async (
      userId: number,
      savingId: number,
      payload: Pick<
        IMemberSaving,
        'client_cost_savings' | 'member_cost_savings' | 'status'
      >
    ): Promise<{ success: boolean }> => {
      return postMemberSaving(userId, savingId, {
        client_cost_savings: normalizeSavingsAmount(
          payload.client_cost_savings
        ),
        member_cost_savings: normalizeSavingsAmount(
          payload.member_cost_savings
        ),
        status: payload.status
      })
    },

    rejectMemberSaving: async (
      userId: number,
      savingId: number,
      current: Pick<
        IMemberSaving,
        'client_cost_savings' | 'member_cost_savings'
      >
    ): Promise<{ success: boolean }> => {
      return postMemberSaving(userId, savingId, {
        client_cost_savings: normalizeSavingsAmount(
          current.client_cost_savings
        ),
        member_cost_savings: normalizeSavingsAmount(
          current.member_cost_savings
        ),
        status: 'REJECT'
      })
    }
  }))
)
